import type { BBox } from '@blackboard/shared';
import { CANVAS_MIN, CANVAS_MAX } from '@blackboard/shared';

export interface Viewport {
  cx: number;
  cy: number;
  zoom: number;
  screenW: number;
  screenH: number;
}

export function initViewport(screenW: number, screenH: number): Viewport {
  return { cx: 0, cy: 0, zoom: 1, screenW, screenH };
}

export function worldToScreen(v: Viewport, wx: number, wy: number): [number, number] {
  return [(wx - v.cx) * v.zoom + v.screenW / 2, (wy - v.cy) * v.zoom + v.screenH / 2];
}

export function screenToWorld(v: Viewport, sx: number, sy: number): [number, number] {
  return [(sx - v.screenW / 2) / v.zoom + v.cx, (sy - v.screenH / 2) / v.zoom + v.cy];
}

export function viewportBBox(v: Viewport): BBox {
  const halfW = v.screenW / 2 / v.zoom;
  const halfH = v.screenH / 2 / v.zoom;
  return { x1: v.cx - halfW, y1: v.cy - halfH, x2: v.cx + halfW, y2: v.cy + halfH };
}

/** 缩放范围：0.02 时 1280px 屏可容纳 ~64000 世界单位宽——足以一眼看全整个画布
 *  （±50000）；上限 8 用于精修细节。 */
export const ZOOM_MIN = 0.02;
export const ZOOM_MAX = 8;

function clampAxis(v: number, half: number, min: number, max: number): number {
  // 视口比整个画布还宽（极小 zoom）时区间反转，钉在画布中心而不是被
  // max(min...) 推到边界 —— 那正是"复位后画面偏到角落"的来源之一。
  if (half * 2 >= max - min) return (min + max) / 2;
  return Math.max(min + half, Math.min(max - half, v));
}

export function clampViewport(v: Viewport): Viewport {
  const zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v.zoom));
  const halfW = v.screenW / 2 / zoom;
  const halfH = v.screenH / 2 / zoom;
  const cx = clampAxis(v.cx, halfW, CANVAS_MIN, CANVAS_MAX);
  const cy = clampAxis(v.cy, halfH, CANVAS_MIN, CANVAS_MAX);
  return { ...v, cx, cy, zoom };
}

export function pan(v: Viewport, dxScreen: number, dyScreen: number): Viewport {
  return clampViewport({ ...v, cx: v.cx - dxScreen / v.zoom, cy: v.cy - dyScreen / v.zoom });
}

export function zoomAt(v: Viewport, screenX: number, screenY: number, factor: number): Viewport {
  const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v.zoom * factor));
  const [wx, wy] = screenToWorld(v, screenX, screenY);
  // After zoom change, we want (wx, wy) to remain under (screenX, screenY):
  //   (wx - ncx) * newZoom + W/2 = screenX
  //   ncx = wx - (screenX - W/2) / newZoom
  const ncx = wx - (screenX - v.screenW / 2) / newZoom;
  const ncy = wy - (screenY - v.screenH / 2) / newZoom;
  return clampViewport({ ...v, cx: ncx, cy: ncy, zoom: newZoom });
}
